import type { NormalisedHour } from '../daily-obs-types';
import type { CoastalSceneState, TidePointInput } from './coastal-types';
import { buildCoastalSceneState, coastalSmoothness } from './coastal-scene-state';
import { hasCoastalEvidence } from './coastal-evidence';
import { POLZEATH_SURF_LOCATION, surfSunPosition, type SurfSceneLocation } from './surf-location';
import { profileForLocation } from './coastal-profiles';
import { surfMomentsAt } from './surf-moments';
import type { SurfReport } from './surf-reports';
import { surfBreakDirection } from './surf-break-direction';
import { clampSurf, easeSurf, SURF_FACE_OFFSET, SURF_HEADING, SURF_LINEUP,
  surfSetAt, surfSetSurfacePoint, surfShorePoint, surfWaterPoint, type SurfWave } from './surf-water';

export const POLZEATH_SCENE = {
  slug: 'polzeath', beachFacingDeg: 280, beachGradient: 'gentle' as const,
  breakType: 'beach' as const, exposedSectors: [[230, 330]] as [number, number][],
  shelterLevel: 'semi-sheltered',
};

export type SurfDaySummary = {
  waveHeightM: number;
  periodS: number;
  windKmh: number;
  gustKmh: number;
  windDirectionDeg: number;
  swellDirectionDeg: number | null;
  windWaveHeightM: number | null;
  cloudPct: number;
  weatherCode: number;
  rainMm: number;
  visibilityM: number | null;
  waterTempC: number | null;
  airTempC?: number | null;
};

export type SurfVisualiserDay = {
  date: string;
  hours: NormalisedHour[];
  summary: SurfDaySummary | null;
  updatedAt?: string | null;
  waterQuality?: { secchiDepthM: number | null; chlorophyllMgM3: number | null } | null;
  tidePoints?: readonly TidePointInput[] | null;
  score?: number | null;
  hourlyScores?: readonly { hour: number; score: number | null; isDay: boolean }[];
};

export type SurfVisualiserForecast = {
  reports?: readonly SurfReport[];
  days: SurfVisualiserDay[];
  updatedAt: string | null;
  location?: SurfSceneLocation;
};

const finite = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

export function parseSurfDaySummary(value: unknown): SurfDaySummary | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const row = value as Record<string, unknown>;
  const required = ['swellHeightM', 'swellPeriodS', 'windSpeedKmh', 'windGustKmh',
    'windDirectionDeg', 'cloudCoverPct',
    'weatherCode', 'precipitationMm'];
  if (!required.every((key) => finite(row[key]))) return null;
  if ((row.swellHeightM as number) < 0 || (row.swellPeriodS as number) <= 0
    || (row.windSpeedKmh as number) < 0 || (row.windGustKmh as number) < 0) return null;
  return {
    waveHeightM: row.swellHeightM as number, periodS: row.swellPeriodS as number,
    windKmh: row.windSpeedKmh as number, gustKmh: row.windGustKmh as number,
    windDirectionDeg: row.windDirectionDeg as number, swellDirectionDeg: finite(row.swellDirectionDeg) ? row.swellDirectionDeg : null,
    windWaveHeightM: finite(row.windWaveHeightM) ? Math.max(0, row.windWaveHeightM) : null, cloudPct: clampSurf(row.cloudCoverPct as number, 0, 100),
    weatherCode: row.weatherCode as number, rainMm: Math.max(0, row.precipitationMm as number),
    visibilityM: finite(row.visibilityM) ? row.visibilityM : null,
    waterTempC: finite(row.waterTempC) ? row.waterTempC : null,
    airTempC: finite(row.tempC) ? row.tempC : null,
  };
}

export function surfWeekDates(start: string): string[] {
  const date = new Date(`${start}T12:00:00Z`);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(start) || !Number.isFinite(date.getTime()) || date.toISOString().slice(0, 10) !== start) return [];
  return Array.from({ length: 7 }, (_, i) => {
    const day = new Date(date);
    day.setUTCDate(day.getUTCDate() + i);
    return day.toISOString().slice(0, 10);
  });
}

/** Missing marine fields are never interpreted as flat, inviting surf. */
export const hasSurfEvidence = hasCoastalEvidence;

/** Do not interpolate across missing hours, or repeat an edge hour as a forecast. */
export function surfHourAt(hours: readonly NormalisedHour[], position: number): NormalisedHour | null {
  const hour = hours.find((entry) => entry.hour === Math.round(position));
  return hour && hasSurfEvidence(hour) ? hour : null;
}

export function surfStateFromHour(hour: NormalisedHour, hours: readonly NormalisedHour[], date: string,
  location: SurfSceneLocation = POLZEATH_SURF_LOCATION, context?: Pick<SurfVisualiserDay, 'waterQuality' | 'tidePoints'>,
  sceneTime?: { date: string; hour: number }): CoastalSceneState {
  const state = buildCoastalSceneState({ location, profile: profileForLocation(location),
    dateKey: date, hour, tide: { dayHours: hours, points: context?.tidePoints }, waterQuality: context?.waterQuality });
  state.surfDirection = surfBreakDirection(location);
  const sun = surfSunPosition(location, sceneTime?.date ?? date, sceneTime?.hour ?? hour.hour);
  state.weather.sun = sun;
  state.weather.sunElevationDeg = sun?.elevationDeg ?? null;
  if (sun) state.weather.daylight = sun.elevationDeg >= -0.833;
  return state;
}

/** A daytime daily illustration. It deliberately has no hour or invented tide curve. */
export function surfStateFromSummary(summary: SurfDaySummary, date: string, location: SurfSceneLocation = POLZEATH_SURF_LOCATION): CoastalSceneState {
  const sun = surfSunPosition(location, date, 12);
  const angle = location.beachFacingDeg === null ? null : summary.windDirectionDeg - location.beachFacingDeg;
  return {
    surfDirection: surfBreakDirection(location),
    breakerStyle: profileForLocation(location).breakerStyle,
    availability: 'ready', seed: `${location.slug}:${date}`, hour: 12,
    swells: summary.swellDirectionDeg === null ? [] : [{ kind: 'primary', heightM: summary.waveHeightM, periodS: summary.periodS,
      directionDeg: summary.swellDirectionDeg, shoreRelativeDeg: summary.swellDirectionDeg - (location.beachFacingDeg ?? summary.swellDirectionDeg) }],
    combinedWaveHeightM: summary.waveHeightM, combinedPeriodS: summary.periodS,
    cleanliness01: coastalSmoothness(summary.waveHeightM, summary.windWaveHeightM, summary.windKmh, summary.gustKmh, angle),
    tide: { heightM: null, position01: 0.5, state: 'unavailable', source: 'neutral' },
    wind: { speedKmh: summary.windKmh, gustKmh: summary.gustKmh, shoreRelativeDeg: angle ?? 90 },
    weather: { daylight: sun === null || sun.elevationDeg > -0.833, sun, sunElevationDeg: sun?.elevationDeg ?? null, cloud01: summary.cloudPct / 100, rainMm: summary.rainMm,
      visibilityM: summary.visibilityM, temperatureC: summary.airTempC ?? 10, weatherCode: summary.weatherCode },
    water: { sstC: summary.waterTempC, secchiDepthM: null, chlorophyllMgM3: null },
  };
}

export type ColinClip = 'ride' | 'sit' | 'paddle' | 'shore' | 'pop' | 'turn-in' | 'turn-out' | 'settle' | 'recover' | 'paddle-out' | 'celebrate' | 'look' | 'warm' | 'wipeout' | 'sting';
export type SurfOutfit = 'trunks' | 'wetsuit';

/** An illustration choice, not an equipment recommendation. Missing temperature keeps the wetsuit. */
export function surfOutfit(state: CoastalSceneState): SurfOutfit {
  return finite(state.water.sstC) && state.water.sstC >= 20 ? 'trunks' : 'wetsuit';
}

export type SurfMotion = {
  clip: ColinClip;
  pose01: number;
  x: number;
  y: number;
  scale: number;
  rotation: number;
  heading: number;
  progress: number;
  along: number;
  wave: SurfWave;
  riding: boolean;
  description: string;
};

const blend = (a: number, b: number, t: number) => a + (b - a) * t;
const hermite = (a: number, b: number, velocityA: number, velocityB: number, duration: number, t: number) =>
  (2 * t ** 3 - 3 * t * t + 1) * a + (t ** 3 - 2 * t * t + t) * duration * velocityA
  + (-2 * t ** 3 + 3 * t * t) * b + (t ** 3 - t * t) * duration * velocityB;

/** Approach, catch, pop up on the breaking face, ride, sit and turn, then paddle out nose-first. */
export function surfMotion(state: CoastalSceneState, elapsed: number, set = surfSetAt(state, elapsed)): SurfMotion {
  const { phase: p, wave, waves } = set;
  const moments = surfMomentsAt(state, elapsed);
  const overflow = state.reports?.some((report) => report.kind === 'overflow') ?? false;
  const loop = (elapsed * 10 % 12) / 12;
  const local = (from: number, to: number) => clampSurf((p - from) / (to - from));
  const onShore = overflow || state.availability !== 'ready' || !state.weather.daylight
    || state.weather.weatherCode >= 95 || state.wind.speedKmh >= 45 || state.wind.gustKmh >= 65
    || state.combinedWaveHeightM > 3.5 || state.combinedWaveHeightM < 0.25;
  const beach = state.coastGeometry ? surfShorePoint(.68, Math.max(.5, state.tide.position01), state.coastGeometry) : { x: 825, y: 405 };
  if (onShore) return { clip: moments.warm ? 'warm' : 'shore', pose01: moments.warm ? moments.warmPose : loop, x: Math.min(944, beach.x + 55), y: beach.y, scale: 0.78, rotation: 0,
    heading: SURF_HEADING, progress: 1, along: 0.68, wave, riding: false,
    description: overflow ? 'A nearby overflow is reported. Colin waits on shore.' : moments.warm ? 'Colin warms his hands on the beach.' : !state.weather.daylight ? 'Colin is calling it a day.' : 'Colin is watching from the beach.' };

  const rideable = state.combinedWaveHeightM >= 0.55 && (state.cleanliness01 >= 0.42 || moments.wipeout);
  let clip: ColinClip = 'sit';
  let pose01 = loop;
  let progress = SURF_LINEUP.progress;
  let heading = SURF_HEADING + Math.PI;
  let description = 'Colin is waiting for a set.';
  const rideEnd = 0.05 + 0.58 * 1.15 + SURF_FACE_OFFSET;
  const stopped = rideEnd + 1.15 * 0.05 / 2;
  const turnOutStart = moments.wipeout || moments.sting ? 0.70 : moments.celebrate ? 0.69 : 0.63;
  const paddleOutStart = turnOutStart + 0.07;
  if (moments.look && p < 0.115) {
    clip = 'look'; pose01 = moments.lookPose;
    description = 'Colin takes a little look along the coast.';
  }
  if (rideable && p >= 0.12 && p < 0.18) {
    clip = 'turn-in'; pose01 = local(0.12, 0.18);
    heading = SURF_HEADING + Math.PI * (1 - easeSurf(pose01 / 0.72));
    description = 'Colin turns for an incoming set.';
  } else if (rideable && p >= 0.18 && p < 0.34) {
    clip = 'paddle'; pose01 = (local(0.18, 0.34) * 4) % 1;
    progress = hermite(SURF_LINEUP.progress, 0.05 + 0.34 * 1.15 + SURF_FACE_OFFSET, 0, 1.15, 0.16, local(0.18, 0.34));
    heading = SURF_HEADING;
    description = 'The set is approaching. Colin paddles towards the beach.';
  } else if (rideable && moments.wipeout && p >= 0.43 && p < turnOutStart) {
    clip = 'wipeout'; pose01 = local(0.43, turnOutStart);
    const start = 0.05 + 0.43 * 1.15 + SURF_FACE_OFFSET;
    progress = hermite(start, stopped, 1.15, 0, 0.20, local(0.43, 0.63));
    heading = SURF_HEADING;
    description = pose01 < 0.3 ? 'A bumpy wave catches Colin off balance. Splash!'
      : pose01 < 0.7 ? 'Colin keeps hold of his board in the chop.' : 'Colin climbs back on, ready to paddle out.';
  } else if (rideable && p >= 0.34 && p < 0.58) {
    clip = p < 0.385 ? 'pop' : 'ride';
    pose01 = clip === 'pop' ? local(0.34, 0.385) : loop;
    progress = wave.progress + SURF_FACE_OFFSET;
    heading = SURF_HEADING;
    description = clip === 'pop' ? 'The wave is breaking. Colin gets to his feet.' : 'Colin is trimming along the wave face.';
  } else if (rideable && p >= 0.58 && p < 0.63) {
    clip = 'settle'; pose01 = local(0.58, 0.63);
    progress = hermite(rideEnd, stopped, 1.15, 0, 0.05, pose01);
    heading = SURF_HEADING;
    description = 'Colin finishes his ride and sits back on the board.';
  } else if (rideable && moments.sting && p >= 0.63 && p < turnOutStart) {
    clip = 'sting'; pose01 = local(0.63, turnOutStart);
    progress = stopped; heading = SURF_HEADING;
    description = 'Ouch! Colin has a little jellyfish moment.';
  } else if (rideable && moments.celebrate && p >= 0.63 && p < turnOutStart) {
    clip = 'celebrate'; pose01 = local(0.63, turnOutStart);
    progress = stopped; heading = SURF_HEADING;
    description = 'A lovely ride. Colin gives a little cheer.';
  } else if (rideable && p >= turnOutStart && p < paddleOutStart) {
    clip = 'turn-out'; pose01 = local(turnOutStart, paddleOutStart);
    progress = stopped;
    heading = SURF_HEADING + Math.PI * easeSurf(pose01 / 0.72);
    description = 'Colin turns his board back towards the line-up.';
  } else if (rideable && p >= paddleOutStart && p < 0.94) {
    clip = 'paddle-out'; pose01 = (local(paddleOutStart, 0.94) * 6) % 1;
    progress = blend(stopped, SURF_LINEUP.progress, easeSurf(local(paddleOutStart, 0.94)));
    description = 'Colin is paddling back out, nose first.';
  } else if (rideable && p >= 0.94) {
    clip = 'recover'; pose01 = local(0.94, 1);
    description = 'Back in the line-up, Colin sits up to wait.';
  }
  const tide = state.tide.position01;
  // Keep one line down the wave. Return across the bay only while paddling out,
  // after the ride and stationary turn are complete.
  const coastA = surfShorePoint(.63, tide, state.coastGeometry), coastB = surfShorePoint(.73, tide, state.coastGeometry);
  const coastScale = Math.min(1, 65 / Math.hypot(coastB.x - coastA.x, coastB.y - coastA.y));
  const carve = rideable && !moments.wipeout
    ? (0.016 + 0.104 * easeSurf((state.combinedWaveHeightM - 0.65) / 2.1)) * state.cleanliness01 ** 2 * coastScale : 0;
  const direction = state.surfDirection === 'right' ? 1 : -1;
  const alongAt = (phase: number) => {
    const out = clampSurf((phase - paddleOutStart) / (0.94 - paddleOutStart));
    // The return starts and ends flatter than the shoreward movement, so the
    // board smoothly joins the existing turn-out and recover poses.
    const returnAcross = out ** 3 * (10 - 15 * out + 6 * out * out);
    return SURF_LINEUP.along + direction * carve * (easeSurf((phase - 0.385) / 0.195) - returnAcross);
  };
  const along = alongAt(p);
  const returning = clip === 'paddle-out';
  const returnPosition = (phase: number) => blend(stopped, SURF_LINEUP.progress, easeSurf((phase - paddleOutStart) / (0.94 - paddleOutStart)));
  const beforeProgress = returning ? returnPosition(p - .0001) : progress - .000115;
  const afterProgress = returning ? returnPosition(p + .0001) : progress + .000115;
  const alongSlope = (alongAt(p + .0001) - alongAt(p - .0001)) / Math.max(.000001, Math.abs(afterProgress - beforeProgress));
  const alongStep = alongSlope * .04 * (returning ? -1 : 1);
  const point = surfSetSurfacePoint(waves, along, progress, tide, state.coastGeometry);
  const ahead = surfSetSurfacePoint(waves, along + alongStep, progress + 0.04, tide, state.coastGeometry);
  const behind = surfSetSurfacePoint(waves, along - alongStep, progress - 0.04, tide, state.coastGeometry);
  if (clip === 'ride' || returning) {
    const flatAhead = surfWaterPoint(alongAt(p + .0001), afterProgress, tide, state.coastGeometry);
    const flatBehind = surfWaterPoint(alongAt(p - .0001), beforeProgress, tide, state.coastGeometry);
    const tangent = Math.atan2(flatAhead.y - flatBehind.y, flatAhead.x - flatBehind.x);
    const base = SURF_HEADING + (returning ? Math.PI : 0);
    heading = base + Math.atan2(Math.sin(tangent - base), Math.cos(tangent - base));
  }
  const surfaceAngle = Math.atan2(ahead.y - behind.y, ahead.x - behind.x);
  const flatAngle = heading - (returning ? Math.PI : 0);
  return { clip, pose01, ...point, progress, along, wave, heading,
    scale: 0.80 + (progress - SURF_LINEUP.progress) * 0.35,
    rotation: clampSurf(surfaceAngle - (clip === 'ride' || returning ? flatAngle : SURF_HEADING), -0.45, 0.45),
    riding: clip === 'ride' || clip === 'pop', description };
}
